import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import moment from 'moment';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { toast } from 'react-toastify';
import {
  replyToTriage,
  resendTriageNote,
  TriageModel,
  TriageNoteModel,
  TriageRecipient,
  TriageRecipientRole,
  updateTriageStatus,
} from 'data/api/triage.api';
import { TriageType } from 'data/models/user-note.model';
import { replaceFontFamily } from 'utils';
import {
  fullName,
  imageRefusal,
  latestOutboundNote,
  noteAuthorName,
} from 'utils/triage';
import {
  EscalationContacts,
  loadEscalationContacts,
  Person,
  pickablePeople,
} from '../escalationContacts';
import { CARD, isForbidden, LINK_BUTTON_SX } from './EventDetails';
import { FIELD_LABEL_SX, RecipientField } from './RecipientField';
import { statusColours } from './TriageCard';
import {
  ERROR_TEXT_SX,
  MessageEditor,
  PRIMARY_BUTTON_SX,
  TriageTakeover,
} from './TriageTakeover';

const STATUSES = [TriageType.New, TriageType.InProgress, TriageType.Closed];

// The three directions have to be told apart at a glance: what the care team
// sent out, what a recipient sent back, and what never left the building.
const DIRECTION_STYLE = {
  outbound: {
    borderLeft: '3px solid #213B54',
    backgroundColor: '#FFFFFF',
  },
  inbound: {
    borderLeft: '3px solid #356766',
    backgroundColor: '#F3F7F7',
  },
  internal: {
    borderLeft: '3px dashed #DBD8D8',
    backgroundColor: '#FBFAF9',
  },
};

const idsFor = (recipients: TriageRecipient[], role: 'to' | 'cc') =>
  recipients
    .filter((recipient) => recipient.role === role)
    .map((recipient) => recipient.userId);

const sameIds = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const plural = (count: number, word: string) =>
  `${count} ${count === 1 ? word : `${word}s`}`;

interface Party {
  name: string;
  address?: string | null;
}

const HeaderRow: React.FC<{ label: string }> = ({ label, children }) => (
  <Stack direction="row" alignItems="flex-start">
    <Typography
      sx={{
        ...FIELD_LABEL_SX,
        paddingTop: 0,
        fontSize: '13px',
        lineHeight: '20px',
      }}
    >
      {label}
    </Typography>
    <Stack sx={{ flex: 1, minWidth: 0 }}>{children}</Stack>
  </Stack>
);

const PartyLine: React.FC<{ party: Party }> = ({ party }) => (
  <Typography
    sx={{
      fontSize: '13px',
      lineHeight: '20px',
      color: '#7D818C',
      overflowWrap: 'anywhere',
    }}
  >
    <Typography
      component="span"
      sx={{ fontSize: '13px', fontWeight: 600, color: '#213B54' }}
    >
      {party.name}
    </Typography>
    {party.address && party.address !== party.name ? ` ${party.address}` : ''}
  </Typography>
);

const HeaderParties: React.FC<{ label: string; parties: Party[] }> = ({
  label,
  parties,
}) => (
  <HeaderRow label={label}>
    {parties.map((party) => (
      <PartyLine key={`${party.name}-${party.address || ''}`} party={party} />
    ))}
  </HeaderRow>
);

// To wins a duplicate, matching the server's one row per user.
const asRecipients = (
  submitted: { toIds: string[]; ccIds: string[] },
  people: Person[],
): TriageRecipient[] => {
  const roles = new Map<string, TriageRecipientRole>();
  submitted.toIds.forEach((id) => roles.set(id, 'to'));
  submitted.ccIds.forEach((id) => {
    if (!roles.has(id)) roles.set(id, 'cc');
  });
  return [...roles].map(([userId, role]) => {
    const person = people.find((candidate) => candidate.userId === userId);
    return { ...person, userId, role };
  });
};

const asParties = (recipients: TriageRecipient[], role: 'to' | 'cc') =>
  recipients
    .filter((recipient) => recipient.role === role)
    .map((recipient) => ({
      name: fullName(recipient),
      address: recipient.email,
    }))
    .filter((party) => party.name);

// Who a message was from. An outbound note is the rep writing as Betterleave;
// an inbound one is whoever the webhook matched, at the address it arrived
// from.
const fromParty = (note: TriageNoteModel): Party => {
  const author = noteAuthorName(note);
  if (note.direction === 'inbound') {
    return {
      name: author || note.emailFrom || 'A recipient',
      address: note.emailFrom,
    };
  }
  if (note.direction === 'outbound') {
    return { name: `${author || 'Care team'} · Betterleave` };
  }
  return { name: author || 'Care team' };
};

interface ITriageDetailProps {
  triage: TriageModel;
  onBack: () => void;
  onReload: () => void;
  /**
   * Writes a re-addressed recipient set into the shared list, so what the rep
   * sees matches what the server now holds even if the refetch fails.
   */
  onCommitRecipients: (
    triagesId: string,
    recipients: TriageRecipient[],
  ) => void;
  /** A reply to this triage is still on the wire from an earlier view. */
  replyInFlight?: boolean;
  onReplyingChange?: (replying: boolean) => void;
}

export const TriageDetail: React.FC<ITriageDetailProps> = ({
  triage,
  onBack,
  onReload,
  onCommitRecipients,
  replyInFlight,
  onReplyingChange,
}) => {
  const [savingStatus, setSavingStatus] = useState<boolean>(false);
  const [resendingId, setResendingId] = useState<string>('');
  const [replying, setReplying] = useState<boolean>(false);
  // An image still being processed is not in the note yet; replying now would
  // send the message without it.
  const [imagesPending, setImagesPending] = useState<boolean>(false);
  // The editor ignores value changes once it has been typed in, so clearing
  // it after a send means remounting it.
  const [composerKey, setComposerKey] = useState<number>(0);
  const [contacts, setContacts] = useState<EscalationContacts>();
  const [contactsError, setContactsError] = useState<boolean>(false);
  const [contactsAttempt, setContactsAttempt] = useState<number>(0);
  const [toMissing, setToMissing] = useState<boolean>(false);
  // The rep's edits to who the reply goes to. Null until they touch it, so a
  // reload that changes the stored recipients shows through untouched.
  const [readdress, setReaddress] = useState<{
    toIds: string[];
    ccIds: string[];
  } | null>(null);
  const { control, handleSubmit, reset, getValues } = useForm<{ note: string }>(
    { defaultValues: { note: '' } },
  );

  const recipients = triage.recipients || [];
  const storedTo = idsFor(recipients, 'to');
  const storedCc = idsFor(recipients, 'cc');
  const replyTo = readdress?.toIds ?? storedTo;
  const replyCc = readdress?.ccIds ?? storedCc;
  // Only a change is sent as a re-address: it replaces the stored recipients,
  // and the unchanged path is the one every existing thread already takes.
  const readdressed =
    !!readdress && !(sameIds(replyTo, storedTo) && sameIds(replyCc, storedCc));

  const toParties = asParties(recipients, 'to');
  const ccParties = asParties(recipients, 'cc');
  const toNames = toParties.map((party) => party.name);
  // Recipients are stored once per triage, not per message, and a re-address
  // replaces them. Only the latest outbound message is known to have gone to
  // the current set; stamping it on older ones would state something false.
  const addressedNoteId = latestOutboundNote(triage)?.triagesNotesId;

  // Only needed to ADD someone; the current recipients render (and can be
  // removed) from the triage itself, so the composer works before this lands
  // and even if it never does.
  useEffect(() => {
    let live = true;
    setContactsError(false);
    loadEscalationContacts(triage.associatedToId)
      .then((loaded) => live && setContacts(loaded))
      .catch(() => live && setContactsError(true));
    return () => {
      live = false;
    };
  }, [triage.associatedToId, contactsAttempt]);

  const people = useMemo(
    () => pickablePeople(contacts, recipients),
    [contacts, recipients],
  );

  const changeStatus = async (triagesStatus: string) => {
    if (savingStatus || triagesStatus === triage.triagesStatus) return;
    setSavingStatus(true);
    try {
      await updateTriageStatus(triage.triagesId, triagesStatus);
      onReload();
    } catch (error) {
      toast.error(
        isForbidden(error)
          ? 'You do not have permission to change this triage.'
          : 'Could not update the status. Please try again.',
      );
    }
    setSavingStatus(false);
  };

  const resend = async (note: TriageNoteModel) => {
    if (resendingId) return;
    setResendingId(note.triagesNotesId);
    try {
      const resent = await resendTriageNote(
        triage.triagesId,
        note.triagesNotesId,
      );
      if (resent?.emailStatus === 'sent') {
        toast.success(
          `Email delivered to ${plural(recipients.length, 'recipient')}.`,
        );
      } else {
        toast.error('The email failed to send again.');
      }
      onReload();
    } catch (error) {
      toast.error('The email could not be resent. Please try again.');
    }
    setResendingId('');
  };

  const sendReply = async () => {
    if (replying || replyInFlight || imagesPending) return;
    const note = getValues('note');
    if (!replyTo.length) {
      setToMissing(true);
      return;
    }
    const sentTo = new Set([...replyTo, ...replyCc]).size;
    const submitted = readdressed ? { toIds: replyTo, ccIds: replyCc } : null;
    setReplying(true);
    onReplyingChange?.(true);
    try {
      const created = await replyToTriage(
        triage.triagesId,
        note,
        submitted ?? undefined,
      );
      // The server replaced the recipients before it answered. Commit that
      // here, before the composer unlocks, rather than trusting the refetch:
      // if it fails, a stale baseline would make the next unchanged reply omit
      // the ids while showing recipients the server no longer holds.
      if (submitted) {
        onCommitRecipients(triage.triagesId, asRecipients(submitted, people));
        setReaddress(null);
      }
      // Never say sent when it was not. The reply is persisted either way;
      // only the email can fail, and the note it belongs to carries a Retry.
      if (created?.emailStatus === 'sent') {
        toast.success(
          `Reply sent — email delivered to ${plural(sentTo, 'recipient')}.`,
        );
      } else if (created?.emailStatus === 'failed') {
        toast.error('Reply saved, but the email failed to send.');
      } else {
        toast.info('Reply saved. The email is still sending.');
      }
      reset({ note: '' });
      setComposerKey((current) => current + 1);
      onReload();
    } catch (error) {
      toast.error(
        isForbidden(error)
          ? 'You do not have permission to reply to this triage.'
          : imageRefusal(error) ??
              'The reply could not be sent. Please try again.',
      );
    }
    setReplying(false);
    onReplyingChange?.(false);
  };

  const colours = statusColours(triage.triagesStatus);
  const contactsNoOptions = contactsError
    ? 'Contacts could not be loaded'
    : 'No other escalation contacts for this organization';

  return (
    <Stack
      component="form"
      noValidate
      onSubmit={handleSubmit(sendReply)}
      sx={{ height: '100%', minHeight: 0 }}
    >
      <TriageTakeover
        title={triage.subject || triage.triageOptionsType || 'Triage'}
        subtitle={[
          triage.triageOptionsType,
          toNames.length ? `To ${toNames.join(', ')}` : '',
        ]
          .filter(Boolean)
          .join(' · ')}
        backLabel="Back to all triages"
        onBack={onBack}
        // Leaving mid-reply would let the rep reopen the thread and send an
        // overlapping reply before this one lands.
        backDisabled={replying}
        action={
          <Stack direction="row" alignItems="center" gap={0.75}>
            {savingStatus && (
              <CircularProgress size={14} sx={{ color: '#356766' }} />
            )}
            <Select
              size="small"
              value={triage.triagesStatus || ''}
              disabled={savingStatus}
              onChange={(event) =>
                void changeStatus(String(event.target.value))
              }
              inputProps={{ 'aria-label': 'Triage status' }}
              sx={{
                height: '32px',
                borderRadius: '8px',
                backgroundColor: colours.bg,
                color: colours.fg,
                fontSize: '13px',
                fontWeight: 600,
                '& .MuiOutlinedInput-notchedOutline': { border: 0 },
                '& .MuiSelect-icon': { color: colours.fg },
              }}
            >
              {/* A status the API already holds but the set does not name
                  still has to be selectable, or opening the dropdown silently
                  rewrites it. */}
              {!STATUSES.includes(triage.triagesStatus as TriageType) &&
                !!triage.triagesStatus && (
                  <MenuItem value={triage.triagesStatus}>
                    {triage.triagesStatus}
                  </MenuItem>
                )}
              {STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        }
        footer={
          <>
            <Stack sx={{ ...CARD, borderColor: '#DBD8D8' }}>
              <RecipientField
                label="To"
                people={people}
                value={replyTo}
                onChange={(toIds) => {
                  setToMissing(false);
                  setReaddress({ toIds, ccIds: replyCc });
                }}
                disabled={replying}
                loading={!contacts && !contactsError}
                noOptionsText={contactsNoOptions}
                invalid={toMissing && !replyTo.length}
              />
              <RecipientField
                label="Cc"
                people={people}
                value={replyCc}
                onChange={(ccIds) => setReaddress({ toIds: replyTo, ccIds })}
                disabled={replying}
                loading={!contacts && !contactsError}
                noOptionsText={contactsNoOptions}
                placeholder="Optional"
                divider
              />
            </Stack>
            {toMissing && !replyTo.length && (
              <Typography sx={{ ...ERROR_TEXT_SX, marginTop: '-4px' }}>
                Add at least one recipient in To.
              </Typography>
            )}
            {contactsError && (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                sx={{ flexWrap: 'wrap', marginTop: '-4px' }}
              >
                <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                  Could not load this organization’s contacts, so no one new can
                  be added.
                </Typography>
                <Button
                  size="small"
                  onClick={() => setContactsAttempt((current) => current + 1)}
                  sx={LINK_BUTTON_SX}
                >
                  Try again
                </Button>
              </Stack>
            )}
            {readdressed && (
              <Typography
                sx={{ fontSize: '13px', color: '#8A5300', marginTop: '-4px' }}
              >
                Replies from now on go to these recipients.
              </Typography>
            )}
            <MessageEditor
              key={composerKey}
              control={control}
              name="note"
              label="Reply"
              // Locked while the reply is in flight. The composer is cleared
              // on success, so anything typed during the send would be wiped
              // without ever having been sent.
              readOnly={replying}
              minHeight="96px"
              requiredMessage="Add a message before replying."
              onImagesPending={setImagesPending}
              placeholder="Reply to the care team"
            />
            <Button
              type="submit"
              variant="contained"
              disabled={replying || replyInFlight || imagesPending}
              startIcon={
                replying ? (
                  <CircularProgress size={14} sx={{ color: '#FFFFFF' }} />
                ) : undefined
              }
              sx={{ ...PRIMARY_BUTTON_SX, alignSelf: 'flex-start' }}
            >
              {replying ? 'Sending' : imagesPending ? 'Adding image' : 'Reply'}
            </Button>
          </>
        }
      >
        {(triage.notes || []).map((note) => {
          const failed =
            note.direction === 'outbound' && note.emailStatus === 'failed';
          return (
            <Stack
              key={note.triagesNotesId}
              component="article"
              sx={{
                ...CARD,
                ...DIRECTION_STYLE[note.direction],
                flex: 'none',
                padding: '12px 14px',
                gap: '10px',
              }}
            >
              {note.direction === 'internal' && (
                <Typography
                  sx={{ fontSize: '13px', fontWeight: 600, color: '#7D818C' }}
                >
                  Internal note, not emailed
                </Typography>
              )}
              <Stack
                sx={{
                  gap: '2px',
                  paddingBottom: '10px',
                  borderBottom: '1px solid #EFEDEB',
                }}
              >
                <HeaderParties label="From" parties={[fromParty(note)]} />
                <HeaderRow label="Sent">
                  <Typography
                    sx={{
                      fontSize: '13px',
                      lineHeight: '20px',
                      color: '#213B54',
                    }}
                  >
                    {moment(note.createdDate).format('MMM D, YYYY · h:mm A')}
                  </Typography>
                </HeaderRow>
                {note.triagesNotesId === addressedNoteId &&
                  !!toParties.length && (
                    <HeaderParties label="To" parties={toParties} />
                  )}
                {note.triagesNotesId === addressedNoteId &&
                  !!ccParties.length && (
                    <HeaderParties label="Cc" parties={ccParties} />
                  )}
              </Stack>

              <Stack
                sx={{
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: '#213B54',
                  overflowWrap: 'anywhere',
                  '& img': {
                    maxWidth: '100%',
                    height: 'auto',
                    borderRadius: '6px',
                  },
                }}
              >
                {parse(DOMPurify.sanitize(replaceFontFamily(note.note || '')))}
              </Stack>

              {note.direction === 'outbound' && note.emailStatus === 'sent' && (
                <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                  Email delivered
                </Typography>
              )}
              {note.direction === 'outbound' &&
                note.emailStatus === 'pending' && (
                  <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
                    Email sending
                  </Typography>
                )}
              {failed && (
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ flexWrap: 'wrap' }}
                >
                  <Typography sx={{ fontSize: '13px', color: '#B42318' }}>
                    Email failed to send
                  </Typography>
                  <Button
                    size="small"
                    disabled={!!resendingId}
                    onClick={() => void resend(note)}
                    sx={LINK_BUTTON_SX}
                  >
                    {resendingId === note.triagesNotesId ? 'Retrying' : 'Retry'}
                  </Button>
                </Stack>
              )}
            </Stack>
          );
        })}
      </TriageTakeover>
    </Stack>
  );
};
